import React, { useState, useRef, useEffect } from 'react';
import './PanelAnnotator.css';
import { saveAnnotations } from '../api/saveAnnotations';
import { useSelector } from "react-redux";
import { selectPanelList } from "../features/panels/panelsSlice";
import {useParams} from "react-router-dom";

const PanelAnnotator = () => {
  const id = useParams();
  const data = useSelector(selectPanelList);
  console.log(data)
  const image_data = data.map(panel => ({
    panel_composite_image: panel.panel_composite_image
  }));
  const [currentPanelIndex, setCurrentPanelIndex] = useState(0);
  const [allAnnotations, setAllAnnotations] = useState({});
  const [isDrawing, setIsDrawing] = useState(false);
  const [startPoint, setStartPoint] = useState({ x: 0, y: 0 });
  const [currentRect, setCurrentRect] = useState(null);
  const [zoomLevel, setZoomLevel] = useState(0.5); 
  const [selectedBoxIndex, setSelectedBoxIndex] = useState(null); // Tracking selected box index for moving/resizing
  const [dragging, setDragging] = useState(false); // Track dragging state of boxes
  const [resizing, setResizing] = useState({ index: null, direction: null }); 
  const imageRef = useRef(null);
  const drawOverlayRef = useRef(null);

  const imageDisplayAreaRef = useRef(null);

  const [isSaving, setIsSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState(null); // { type: 'success' | 'error', message: string } | null

  const [allHorizontalLines, setAllHorizontalLines] = useState({});
  const [draggingLine, setDraggingLine] = useState({ index: null, startY: 0, initialLineY: 0 });

  const horizontalLines = allHorizontalLines[currentPanelIndex] || [];

  useEffect(() => {
    // Initialize annotations and horizontal lines from data submitted earlier, also handles the logic of changing the y-coordinates of boxes since when keeping them in db, we had adjusted y-coordinates according to splits.
    if (data && data.length > 0) {
      const initialAnnotations = {};
      const initialHorizontalLines = {};
      data.forEach((panel, index) => {
        if (panel.panel_annotations && Array.isArray(panel.panel_annotations.annotations)) {
          const sortedLines = panel.panel_annotations.lines ? [...panel.panel_annotations.lines].sort((a, b) => a - b) : [];
          
          const restoredAnnotations = panel.panel_annotations.annotations.map(anno => {
            const splitId = anno.split_id || 1;
            let yOffset = 0;
            if (splitId > 1 && sortedLines.length >= splitId - 1) {
              yOffset = sortedLines[splitId - 2];
            }
            return { ...anno, y: anno.y + yOffset };
          });
          initialAnnotations[index] = restoredAnnotations;
        } else {
          initialAnnotations[index] = [];
        }
        if (panel.panel_annotations && Array.isArray(panel.panel_annotations.lines)) {
          initialHorizontalLines[index] = panel.panel_annotations.lines;
        }
      });
      setAllAnnotations(initialAnnotations);
      setAllHorizontalLines(initialHorizontalLines);
    }
  }, [data]);

  useEffect(() => {
    setCurrentRect(null);
    setZoomLevel(0.5); 
    setSaveStatus(null);
  }, [currentPanelIndex]);

  useEffect(() => {
    if (allHorizontalLines[currentPanelIndex] !== undefined) {
      return;
    }

    const image = imageRef.current;
    if (image) {
      const calculateLines = () => {
        const imageHeight = image.naturalHeight;
        const lines = [];
        for (let i = 1; i * 1280 < imageHeight; i++) {
          lines.push(i * 1280);
        }
        setAllHorizontalLines(prev => ({ ...prev, [currentPanelIndex]: lines }));
      };

      if (image.complete) {
        calculateLines();
      } else {
        image.addEventListener('load', calculateLines);
      }
      return () => {
        image.removeEventListener('load', calculateLines);
      };
    } else {
      setAllHorizontalLines(prev => ({ ...prev, [currentPanelIndex]: [] }));
    }
  }, [data, currentPanelIndex, allHorizontalLines]);

  useEffect(() => {
    if (draggingLine.index === null) return;

    const handleMouseMove = (event) => {
      const dy = (event.clientY - draggingLine.startY) / zoomLevel;

      setAllHorizontalLines(prevAllLines => {
        const newAllLines = { ...prevAllLines };
        const currentPanelLines = [...(newAllLines[currentPanelIndex] || [])];
        currentPanelLines[draggingLine.index] = draggingLine.initialLineY + dy;
        newAllLines[currentPanelIndex] = currentPanelLines;
        return newAllLines;
      });
    };

    const handleMouseUp = () => {
      setDraggingLine({ index: null, startY: 0, initialLineY: 0 });
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [draggingLine, zoomLevel, currentPanelIndex]);

  useEffect(() => {
    if (dragging) {
      const handleMouseMove = (event) => {
        const dx = event.movementX / zoomLevel;
        const dy = event.movementY / zoomLevel;

        const currentPanelAnnotations = [...(allAnnotations[currentPanelIndex] || [])];
        const box = currentPanelAnnotations[selectedBoxIndex];

        currentPanelAnnotations[selectedBoxIndex] = {
          ...box,
          x: box.x + dx,
          y: box.y + dy,
        };

        setAllAnnotations({
          ...allAnnotations,
          [currentPanelIndex]: currentPanelAnnotations,
        });
      };

      const handleMouseUp = () => {
        setDragging(false);
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };

      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);

      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [dragging, selectedBoxIndex, allAnnotations, currentPanelIndex, zoomLevel]);

  useEffect(() => {
    if (resizing.index !== null) {
      const handleMouseMove = (event) => {
        const dx = event.movementX / zoomLevel;
        const dy = event.movementY / zoomLevel;

        const currentPanelAnnotations = [...(allAnnotations[currentPanelIndex] || [])];
        const box = currentPanelAnnotations[resizing.index];

        let newBox = { ...box };

        if (resizing.direction === 'right') {
          newBox.width = Math.max(0, box.width + dx);
        } else if (resizing.direction === 'bottom') {
          newBox.height = Math.max(0, box.height + dy);
        } else if (resizing.direction === 'bottom-right') {
          newBox.width = Math.max(0, box.width + dx);
          newBox.height = Math.max(0, box.height + dy);
        }

        currentPanelAnnotations[resizing.index] = newBox;

        setAllAnnotations({
          ...allAnnotations,
          [currentPanelIndex]: currentPanelAnnotations,
        });
      };

      const handleMouseUp = () => {
        setResizing({ index: null, direction: null });
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };

      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);

      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [resizing, allAnnotations, currentPanelIndex, zoomLevel]);

  const handleMouseDown = (event) => {
    if (!drawOverlayRef.current) return;

    const rect = drawOverlayRef.current.getBoundingClientRect();
    const x = (event.clientX - rect.left) / zoomLevel;
    const y = (event.clientY - rect.top) / zoomLevel;

    const currentPanelAnnotations = allAnnotations[currentPanelIndex] || [];

    // Check if the click is inside an existing box
    const clickedBoxIndex = currentPanelAnnotations.findIndex(
      (box) =>
        x >= box.x &&
        x <= box.x + box.width &&
        y >= box.y &&
        y <= box.y + box.height
    );

    if (clickedBoxIndex !== -1) {
      // If the click is inside a box, enable dragging
      setSelectedBoxIndex(clickedBoxIndex);
      setDragging(true);
      setStartPoint({ x, y });
    } else {
      // If the click is outside all boxes, start creating a new box
      setIsDrawing(true);
      setStartPoint({ x, y });
      setCurrentRect({ x, y, width: 0, height: 0 });
    }
  };

  const handleMouseMove = (event) => {
    if (!drawOverlayRef.current) return;

    const rect = drawOverlayRef.current.getBoundingClientRect();
    const currentX = (event.clientX - rect.left) / zoomLevel;
    const currentY = (event.clientY - rect.top) / zoomLevel;

    if (isDrawing && currentRect) {
      // Update the dimensions of the new box while drawing
      const width = currentX - startPoint.x;
      const height = currentY - startPoint.y;

      setCurrentRect({
        x: width > 0 ? startPoint.x : currentX,
        y: height > 0 ? startPoint.y : currentY,
        width: Math.abs(width),
        height: Math.abs(height),
      });
    } else if (dragging && selectedBoxIndex !== null) {
      // Update the position of the selected box while dragging
      const dx = currentX - startPoint.x;
      const dy = currentY - startPoint.y;

      const currentPanelAnnotations = [...(allAnnotations[currentPanelIndex] || [])];
      const box = currentPanelAnnotations[selectedBoxIndex];

      currentPanelAnnotations[selectedBoxIndex] = {
        ...box,
        x: box.x + dx,
        y: box.y + dy,
      };

      setAllAnnotations({
        ...allAnnotations,
        [currentPanelIndex]: currentPanelAnnotations,
      });

      setStartPoint({ x: currentX, y: currentY });
    }
  };

  const handleMouseUp = () => {
    if (isDrawing && currentRect) {
      // Finalize the new box
      if (currentRect.width > 0 && currentRect.height > 0) {
        const currentPanelAnnotations = allAnnotations[currentPanelIndex] || [];
        setAllAnnotations({
          ...allAnnotations,
          [currentPanelIndex]: [...currentPanelAnnotations, currentRect],
        });
      }
      setIsDrawing(false);
      setCurrentRect(null);
    }

    if (dragging) {
      // Stop dragging
      setDragging(false);
    }
  };

  const handleLineMouseDown = (event, index) => {
    event.stopPropagation(); // Prevent canvas mousedown from firing
    setDraggingLine({
      index: index,
      startY: event.clientY,
      initialLineY: horizontalLines[index],
    });
  };

  const handleBoxSelect = (index) => {
    setSelectedBoxIndex(index);
  };

  const handleResizeStart = (index, direction) => {
    setResizing({ index, direction });
  };

  const handleMouseLeave = () => {};

  const handleNextPanel = () => {
    if (currentPanelIndex < image_data.length - 1) {
      setCurrentPanelIndex(currentPanelIndex + 1);
    }
  };

  const handlePrevPanel = () => {
    if (currentPanelIndex > 0) {
      setCurrentPanelIndex(currentPanelIndex - 1);
    }
  };

  const handleZoomIn = () => {
    setZoomLevel((prevZoom) => Math.min(prevZoom + 0.1, 3));
  };

  const handleZoomOut = () => {
    setZoomLevel((prevZoom) => Math.max(prevZoom - 0.1, 0.2));
  };

  const handleResetZoom = () => {
    setZoomLevel(1); 
  };

  const handleDeleteSelectedBox = () => {
    if (selectedBoxIndex === null || isSaving) return;

    const currentPanelAnnotations = [...(allAnnotations[currentPanelIndex] || [])];
    currentPanelAnnotations.splice(selectedBoxIndex, 1); 

    setAllAnnotations({
      ...allAnnotations,
      [currentPanelIndex]: currentPanelAnnotations,
    });

    setSelectedBoxIndex(null); // Clear the selection after deletion
  };

  const handleSaveAnnotations = async () => {
    if (!image_data || image_data.length === 0 || !image_data[currentPanelIndex]) {
      setSaveStatus({ type: 'error', message: 'No panel image_data to save for.' });
      return;
    }

    setIsSaving(true);
    setSaveStatus(null); 

    try {
      const currentAnnotations = allAnnotations[currentPanelIndex] || [];

      const dataToSave = {
        annotations: currentAnnotations,
        lines: horizontalLines,
      };

      console.log("Data to save:", dataToSave);

      const result = await saveAnnotations(dataToSave, currentPanelIndex, id);

      if (result.success) {
        setSaveStatus({ type: 'success', message: 'Annotations saved successfully!' });
        setTimeout(() => setSaveStatus(null), 3000);
      } else {
        setSaveStatus({ type: 'error', message: result.error || 'Failed to save annotations' });
      }
    } catch (error) {
      console.error('Error saving annotations:', error);
      setSaveStatus({ type: 'error', message: error.message || 'An unexpected error occurred.' });
    } finally {
      setIsSaving(false);
    }
  };

  const handleCreateNewBox = () => {
    if (!drawOverlayRef.current || !imageDisplayAreaRef.current) {
      console.error("Canvas or display area reference is not available.");
      return;
    }

    const displayArea = imageDisplayAreaRef.current;
    const overlay = drawOverlayRef.current;

    const mediumBoxWidth = 100; // Width of the new box
    const mediumBoxHeight = 100; // Height of the new box

    // Calculate the center of the visible screen
    const visibleCenterX =
      displayArea.scrollLeft +
      displayArea.clientWidth / 2
    const visibleCenterY =
      displayArea.scrollTop +
      displayArea.clientHeight / 2

    // Adjust the box position to center it in the visible area
    const newBox = {
      x: visibleCenterX - mediumBoxWidth / 2,
      y: visibleCenterY - mediumBoxHeight / 2,
      width: mediumBoxWidth,
      height: mediumBoxHeight,
    };

    const currentPanelAnnotations = allAnnotations[currentPanelIndex] || [];
    setAllAnnotations({
      ...allAnnotations,
      [currentPanelIndex]: [...currentPanelAnnotations, newBox],
    });

    setSelectedBoxIndex(currentPanelAnnotations.length); // Select the newly created box
  };

  if (!image_data || image_data.length === 0) {
    return <p>No image_data provided for annotation.</p>;
  }

  const currentPanel = image_data[currentPanelIndex];
  const imageUrl = currentPanel?.panel_composite_image;

  const displayedAnnotations = allAnnotations[currentPanelIndex] || [];

  return (
    <div className="panel-annotator-container">
      <div className="zoom-controls">
      <button onClick={handlePrevPanel} disabled={isSaving || currentPanelIndex === 0}>
          Previous
        </button>

        <button onClick={handleZoomIn} disabled={isSaving}>Zoom In (+)</button>
        <button onClick={handleZoomOut} disabled={isSaving}>Zoom Out (-)</button>
        <button onClick={handleResetZoom} disabled={isSaving}>Reset Zoom (100%)</button>
        <span>Zoom: {Math.round(zoomLevel * 100)}%</span>
        <button 
          onClick={handleSaveAnnotations} 
          disabled={isSaving || displayedAnnotations.length === 0}
          className="save-annotations-button"
        >
          {isSaving ? 'Saving...' : 'Save Annotations'}
        </button>
        <button 
          onClick={handleDeleteSelectedBox} 
          disabled={selectedBoxIndex === null || isSaving}
          className="delete-selected-box-button"
        >
          Delete Selected Box
        </button>
        <button 
          onClick={handleCreateNewBox} 
          disabled={isSaving}
          className="create-new-box-button"
        >
          Create New Box
        </button>
        <button
          onClick={handleNextPanel}
          disabled={isSaving || currentPanelIndex === image_data.length - 1}
        >
          Next
        </button>
      </div>

      {saveStatus && (
        <div className={`save-status-message ${saveStatus.type}`}>
          {saveStatus.message}
          {saveStatus.type === 'error' && (
            <button onClick={() => setSaveStatus(null)} className="close-status-button">×</button>
          )}
        </div>
      )}

      <div className="main-content-area">
        <div className="image-display-area" ref={imageDisplayAreaRef}>
          <div
            className="image-wrapper"
            ref={drawOverlayRef}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseLeave}
            style={{
              transform: `scale(${zoomLevel})`,
              transformOrigin: 'center center',
              transition: isSaving ? 'none' : 'transform 0.2s ease-out',
            }}
          >
            {imageUrl ? (
              <img
                ref={imageRef}
                src={imageUrl}
                alt={`Panel ${currentPanelIndex + 1}`}
                className="annotatable-image"
                draggable="false"
              />
            ) : (
              <p>Image not available for this panel.</p>
            )}
            {/* Render horizontal lines */}
            {horizontalLines.map((lineY, index) => (
              <div
                key={`hline-${index}`}
                className="horizontal-line"
                style={{ top: `${lineY}px` }}
                onMouseDown={(e) => handleLineMouseDown(e, index)}
              />
            ))}
            {displayedAnnotations.map((anno, index) => (
              <div
                key={`anno-${currentPanelIndex}-${index}`}
                className={`annotation-box ${selectedBoxIndex === index ? 'selected' : ''}`}
                style={{
                  left: `${anno.x}px`,
                  top: `${anno.y}px`,
                  width: `${anno.width}px`,
                  height: `${anno.height}px`,
                }}
                onMouseDown={(e) => {
                  e.stopPropagation();
                  handleBoxSelect(index);
                  setDragging(true);
                }}
              >
                <span className="annotation-box-number">{index + 1}</span>
                {/* Add resize handles */}
                <div
                  className="resize-handle resize-handle-right"
                  onMouseDown={(e) => {
                    e.stopPropagation();
                    handleResizeStart(index, 'right');
                  }}
                />
                <div
                  className="resize-handle resize-handle-bottom"
                  onMouseDown={(e) => {
                    e.stopPropagation();
                    handleResizeStart(index, 'bottom');
                  }}
                />
                <div
                  className="resize-handle resize-handle-bottom-right"
                  onMouseDown={(e) => {
                    e.stopPropagation();
                    handleResizeStart(index, 'bottom-right');
                  }}
                />
              </div>
            ))}
            {isDrawing && currentRect && (
              <div
                className="annotation-box current-drawing"
                style={{
                  left: `${currentRect.x}px`,
                  top: `${currentRect.y}px`,
                  width: `${currentRect.width}px`,
                  height: `${currentRect.height}px`,
                }}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PanelAnnotator;
